"use client";

import type React from "react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Flex,
  IconButton,
  Text,
  VStack,
  Accordion,
  useToast,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { ArrowLeft, Copy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RetellWebClient } from "retell-client-js-sdk";
import {
  agentService,
  type Agent,
  type VoiceDetails,
} from "../../services/agentService.ts";
import { VoiceSelect } from "./VoiceSelect.tsx";
import { LanguageSelect } from "./LanguageSelect.tsx";
import { useAppTheme } from "../../ThemeContext.tsx";
import { callService } from "../../services/callService.ts";
import { WebhookSettings } from "./WebhookSettings.tsx";
import { SecuritySettings } from "./SecuritySettings.tsx";
import { CallSettings } from "./call-settings/CallSettings.tsx";
import { PostCallAnalysis } from "./post-call-analysis/PostCallAnalysis.tsx";
import { SpeechSettings } from "./speech-settings/SpeechSettings.tsx";
import { DynamicVariablesButton } from "./DynamicVariablesButton.tsx";
import { CustomLLMUrlInput } from "./CustomLLMUrlInput.tsx";
import { EditableAgentName } from "./EditableAgentName.tsx";

interface CustomLLMConfigProps {
  agent: Agent;
}

function getShortId(id) {
  if (!id) return "";
  const len = id.length;
  return len > 5 ? `${id.slice(0, 2)}...${id.slice(-3)}` : id;
}

export const CustomLLMConfig: React.FC<CustomLLMConfigProps> = ({
  agent: initialAgent,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const retellWebClientRef = useRef<RetellWebClient | null>(null);

  const [agent] = useState<Agent>(initialAgent);
  const [, setIsLoading] = useState(false);
  const [voices, setVoices] = useState<VoiceDetails[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<VoiceDetails | null>(null);
  const [isCallStarting, setIsCallStarting] = useState(false);
  const [, setCallData] = useState<{
    accessToken: string;
    callId: string;
  } | null>(null);

  const [isCallActive, setIsCallActive] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<
    Record<string, string>
  >({});
  const [, setSelectedVoiceState] = useState<VoiceDetails | null>(null);

  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const voicesResponse = await agentService.listVoices();
        setVoices(voicesResponse);
        const foundVoice = voicesResponse.find(
          (v) => v.voice_id === agent.voice_id
        );
        if (foundVoice) {
          setSelectedVoice(foundVoice);
          setSelectedVoiceState(foundVoice); // Also update the internal state in VoiceSelect
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast({
          title: "Error loading data",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [agent, toast]);

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      toast({
        title: "Copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    },
    [toast],
  );

  const initializeRetellWebClient = useCallback(() => {
    if (!retellWebClientRef.current) {
      retellWebClientRef.current = new RetellWebClient();
      retellWebClientRef.current.on("call_started", () => {
        console.log("call started");
        setIsCallActive(true);
      });
      retellWebClientRef.current.on("call_ended", () => {
        console.log("call ended");
        setIsCallActive(false);
        setCallData(null);
      });
      retellWebClientRef.current.on("agent_start_talking", () => {
        console.log("agent_start_talking");
      });
      retellWebClientRef.current.on("agent_stop_talking", () => {
        console.log("agent_stop_talking");
      });
      retellWebClientRef.current.on("error", (error) => {
        console.error("An error occurred:", error);
        toast({
          title: "Call Error",
          description:
            error.response?.data?.message ||
            "An error occurred during the call",
          status: "error",
          duration: 5000,
        });
        retellWebClientRef.current?.stopCall();
      });
    }
  }, [toast]);

  const joinCall = useCallback(
    async (accessToken: string) => {
      if (!retellWebClientRef.current) {
        initializeRetellWebClient();
      }
      try {
        await retellWebClientRef.current?.startCall({
          accessToken,
          sampleRate: 24000,
        });
        toast({
          title: "Joined call successfully",
          status: "success",
          duration: 3000,
        });
      } catch (error) {
        console.error("Error joining call:", error);
        toast({
          title: "Error joining call",
          description:
            error instanceof Error
              ? error.message
              : "An unknown error occurred",
          status: "error",
          duration: 5000,
        });
      }
    },
    [initializeRetellWebClient, toast]
  );

  const handleStartCall = useCallback(async () => {
    setIsCallStarting(true);

    try {
      const callOptions: any = {};

      if (Object.keys(dynamicVariables).length > 0) {
        callOptions.retell_llm_dynamic_variables = dynamicVariables;
      }

      const response = await callService.createWebCall(
        agent.agent_id,
        callOptions
      );
      const newCallData = {
        accessToken: response.access_token,
        callId: response.call_id,
      };
      setCallData(newCallData);
      toast({
        title: "Web call created",
        description: `Call ID: ${response.call_id}`,
        status: "success",
        duration: 5000,
      });
      await joinCall(response.access_token);
    } catch (error) {
      console.error("Error starting web call:", error);
      toast({
        title: "Error starting web call",
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsCallStarting(false);
    }
  }, [agent.agent_id, toast, joinCall, dynamicVariables]);

  const handleEndCall = useCallback(() => {
    retellWebClientRef.current?.stopCall();
    setIsCallActive(false);
    setCallData(null);
  }, []);

  const handleDynamicVariablesChange = useCallback(
    (vars: Record<string, string>) => {
      if (Object.keys(vars).length > 0) {
        console.log("Variables updated:", vars);
      }
      setDynamicVariables(vars);
    },
    []
  );

  const testPanel = useMemo(
    () => (
      <VStack
        w="250px"
        p={4}
        borderLeft="1px solid"
        borderColor={isDark ? "gray.200" : "gray.700"}
        spacing={4}
      >
        <Flex
          align="center"
          justify="space-between"
          w="full"
          bg={isDark ? "gray.800" : "gray.200"}
        >
          <Text px={3} py={1} rounded="md" fontWeight="semibold">
            Test your agent
          </Text>
          <DynamicVariablesButton
            onVariablesChange={handleDynamicVariablesChange}
            prompt=""
            beginMessage=""
          />
        </Flex>

        <Box textAlign="center" py={8}>
          <Box as="span" fontSize="48px">
            🎙️
          </Box>
          <Box
            mt={4}
            w="full"
            onClick={isCallActive ? handleEndCall : handleStartCall}
            isLoading={isCallStarting}
            loadingText="Starting call..."
            colorScheme="gray"
            bg="transparent"
            textColor={isDark ? "whiteAlpha.900" : "gray.800"}
            _hover={{ bg: isDark ? "gray.800" : "gray.100" }}
            cursor="pointer"
            textAlign="center"
            py={2}
            borderRadius="md"
          >
            {isCallActive ? "End Call" : "Start Call"}
          </Box>
        </Box>
      </VStack>
    ),
    [
      isDark,
      isCallActive,
      handleEndCall,
      handleStartCall,
      isCallStarting,
      handleDynamicVariablesChange,
    ]
  );

  const mainContent = useMemo(
    () => (
      <Flex
        h="100vh"
        bg={isDark ? "gray.900" : "gray.100"}
        color={isDark ? "white" : "gray.800"}
        direction="column"
      >
        <Flex
          px={4}
          py={3}
          borderBottom="1px solid"
          borderColor={isDark ? "gray.700" : "gray.200"}
          direction="column"
        >
          <Flex align="center">
            <IconButton
              aria-label="Back"
              icon={<ArrowLeft />}
              variant="ghost"
              color={isDark ? "white" : "gray.800"}
              _hover={{ bg: isDark ? "gray.700" : "gray.200" }}
              onClick={() => navigate("/agents")}
            />
            <Box ml={4}>
              <EditableAgentName
                agentId={agent.agent_id}
                initialName={agent.agent_name}
              />
              <Text color="gray.500" fontSize="sm">
                Custom LLM &bull; Agent ID: {getShortId(agent.agent_id)}{" "}
                <IconButton
                  aria-label="Copy Agent ID"
                  icon={<Copy size={12} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => copyToClipboard(agent.agent_id)}
                  color="gray.500"
                />
              </Text>
            </Box>
          </Flex>
        </Flex>

        <Flex
          px={4}
          py={2}
          borderBottom="1px solid"
          borderColor={isDark ? "gray.700" : "gray.200"}
          gap={2}
        >
          <VoiceSelect
            initialVoice={selectedVoice}
            voices={voices}
            agentId={agent.agent_id}
            setSelectedVoice={setSelectedVoice}
          />
          <LanguageSelect
            initialLanguage={agent.language}
            agentId={agent.agent_id}
          />
        </Flex>

        <Flex flex={1} overflow="hidden" alignItems="stretch">
          <Box
            flex={1}
            p={4}
            borderRight="1px solid"
            borderColor={isDark ? "gray.700" : "gray.200"}
          >
            <VStack align="stretch" h="full" spacing={4}>
              <CustomLLMUrlInput
                agentId={agent.agent_id}
                initialUrl={
                  (agent.response_engine as any).llm_websocket_url ||
                  "https://replace-with-your-llm-url.com"
                }
              />

              <Alert
                bg="transparent"
                status="info"
                rounded="md"
                marginTop={-10}
                marginLeft={-3}
              >
                <AlertIcon
                  color={isDark ? "gray.300" : "gray.500"}
                  height={3}
                />
                <Text fontSize="xs" marginLeft={-3}>
                  Fully customize response generation with your LLM.
                  {/* <Text
                    as="a"
                    href="https://docs.retellai.com/integrate-llm/overview"
                    target="_blank"
                    rel="noopener noreferrer"
                    ml={1}
                    color={isDark ? "gray.300" : "gray.500"}
                    cursor="pointer"
                  >
                    (wiki)
                  </Text> */}
                </Text>
              </Alert>
            </VStack>
          </Box>

          <VStack
            w="300px"
            spacing={0}
            overflow="auto"
            p={4}
            borderRight="1px solid"
            borderColor={isDark ? "gray.700" : "gray.200"}
            sx={{
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: isDark ? "black" : "white",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: isDark ? "gray.600" : "gray.300",
                borderRadius: "6px",
                border: `2px solid ${isDark ? "black" : "white"}`,
              },
            }}
          >
            <Accordion allowMultiple w="full">
              <SpeechSettings agentId={agent.agent_id} />
              <CallSettings
                agentId={agent.agent_id}
                initialSettings={{
                  enable_voicemail_detection: agent.enable_voicemail_detection,
                  end_call_after_silence_ms: agent.end_call_after_silence_ms,
                  max_call_duration_ms: agent.max_call_duration_ms,
                  begin_message_delay_ms: agent.begin_message_delay_ms,
                }}
              />
              <PostCallAnalysis
                agentId={agent.agent_id}
                initialAnalysisData={agent.post_call_analysis_data}
              />
              <SecuritySettings
                agentId={agent.agent_id}
                initialOptOut={agent.opt_out_sensitive_data_storage || false}
                initialFallbackVoiceIds={agent.fallback_voice_ids || null}
                agent={agent}
              />
              <WebhookSettings
                agentId={agent.agent_id}
                initialWebhookUrl={agent.agent_webhook_url}
              />
            </Accordion>
          </VStack>

          {testPanel}
        </Flex>
      </Flex>
    ),
    [agent, voices, selectedVoice, isDark, navigate, testPanel, copyToClipboard]
  );

  return mainContent;
};
